import React from "react";
import { motion } from "framer-motion";
import { ChevronRight, Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import mockupLeft from "../../assets/beauty/mockup-left.png";
import mockupCenter from "../../assets/beauty/mockup-center.png";
import mockupRight from "../../assets/beauty/mockup-right.png";
import noise from "../../assets/beauty/noise.png";

const green = "#43BD98";
const pink = "#D9B8CE";

function NotificationCard({
	title,
	line1,
	line2,
	className,
	delay = 1,
	floatDelay = 1.5,
	direction = "y",
	float = true,
}) {
	return (
		<motion.div
			className={`absolute z-40 pointer-events-none drop-shadow-xl ${className}`}
			initial={{
				opacity: 0,
				scale: 0.9,
				...(direction === "x" ? { x: -28 } : { y: -28 }),
			}}
			animate={{
				opacity: 1,
				scale: 1,
				x: 0,
				y: float
					? direction === "y"
						? [0, -6, 0]
						: [0, 5, 0]
					: 0,
			}}
			transition={{
				opacity: { duration: 0.55, delay },
				scale: {
					duration: 0.55,
					delay,
					type: "spring",
					stiffness: 240,
					damping: 16,
				},
				x: { duration: 0.55, delay },
				y: float
					? {
							duration: direction === "y" ? 3.8 : 4.2,
							repeat: Infinity,
							ease: "easeInOut",
							delay: floatDelay,
						}
					: { duration: 0.55, delay },
			}}
		>
			<div
				className="flex items-center gap-2.5 rounded-2xl px-3 py-2.5 border shadow-lg md:backdrop-blur-md"
				style={{
					background: "rgba(255,255,255,0.97)",
					borderColor: "rgba(217,184,206,0.35)",
				}}
			>
				<div
					className="w-9 h-9 rounded-full flex-shrink-0 flex items-center justify-center text-white text-xs font-bold"
					style={{
						background: `linear-gradient(135deg, ${pink} 0%, #c9a0b8 100%)`,
					}}
				>
					{title.charAt(0)}
				</div>
				<div className="flex-1">
					<p
						className="text-[11px] font-bold leading-tight whitespace-nowrap"
						style={{ color: "#1a1a1a" }}
					>
						{title}
					</p>
					<p
						className="text-[10px] leading-tight whitespace-nowrap mt-0.5"
						style={{ color: "#555" }}
					>
						{line1}
					</p>
					<p
						className="text-[10px] leading-tight whitespace-nowrap"
						style={{ color: "#999" }}
					>
						{line2}
					</p>
				</div>
				<div
					className="w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center"
					style={{ backgroundColor: `${green}18` }}
				>
					<Check size={13} style={{ color: green }} strokeWidth={3} />
				</div>
			</div>
		</motion.div>
	);
}

export default function Hero2() {
	const { t } = useTranslation();
	const primary = "#D9B8CE";
	const accent = "#43BD98";

	return (
		<section
			id="inicio"
			className="relative overflow-hidden bg-gradient-to-b from-white via-[#fff9fc] to-white text-gray-900"
			style={{ fontFamily: "'Poppins', sans-serif" }}
		>
			{/* Decorative blobs — lighter on mobile to avoid GPU flicker */}
			<div
				className="absolute w-[280px] h-[280px] md:w-[500px] md:h-[500px] bg-[#FCE8F1] rounded-full blur-[60px] md:blur-[140px] opacity-30 md:opacity-40 top-[-120px] left-[-120px] md:top-[-180px] md:left-[-180px]"
				aria-hidden="true"
			/>
			<div
				className="absolute hidden md:block w-[400px] h-[400px] bg-[#E7F8F2] rounded-full blur-[120px] opacity-30 bottom-[-100px] right-[-120px]"
				aria-hidden="true"
			/>
			<div
				className="absolute hidden md:block w-[300px] h-[300px] bg-[#F3E8EF] rounded-full blur-[100px] opacity-25 top-[30%] right-[10%]"
				aria-hidden="true"
			/>

			{/* Noise texture */}
			<div
				className="absolute inset-0 opacity-[0.35] md:opacity-[0.6] pointer-events-none"
				style={{
					backgroundImage: `url(${noise})`,
					backgroundRepeat: "repeat",
					backgroundSize: "250px 250px",
				}}
			/>

			<div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-16 pt-36 md:pt-48 pb-24 grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
				{/* Text content */}
				<div className="text-center md:text-left">
					{/* Badge */}
					<motion.div
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.5, ease: "easeOut" }}
						className="inline-flex items-center mb-6"
					>
						<span
							className="text-xs font-semibold tracking-wide px-4 py-2 rounded-full"
							style={{
								background: `${primary}18`,
								color: primary,
								border: `1px solid ${primary}30`,
							}}
						>
							{t("salon2.hero.badge")}
						</span>
					</motion.div>

					{/* Heading */}
					<motion.h1
						className="font-extrabold mb-6"
						style={{
							fontSize: "clamp(2rem, 5vw, 3.2rem)",
							lineHeight: 1.1,
							color: "#1a1a1a",
						}}
						initial={{ opacity: 0, y: -20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.6, ease: "easeOut", delay: 0.1 }}
					>
						{t("salon2.hero.title.line1")}{" "}
						<span style={{ color: primary }}>
							{t("salon2.hero.title.highlight")}
						</span>{" "}
						{t("salon2.hero.title.line2")}
					</motion.h1>

					{/* Subtitle */}
					<motion.p
						className="text-base md:text-lg max-w-lg mx-auto md:mx-0 leading-relaxed mb-8"
						style={{ color: "#555" }}
						initial={{ opacity: 0, y: 10 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.7, ease: "easeOut", delay: 0.2 }}
					>
						{t("salon2.hero.subtitle")}
					</motion.p>

					{/* CTAs */}
					<motion.div
						className="flex justify-center md:justify-start gap-4 flex-wrap mb-6"
						initial={{ opacity: 0, y: 10 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.7, ease: "easeOut", delay: 0.35 }}
					>
						<a
							href="https://creator.applaby.com?onboarding=true&appType=Services&appSubtype=Salon"
							target="_blank"
							rel="noreferrer"
							className="px-8 py-3.5 rounded-xl font-semibold shadow-md hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center gap-2 text-white"
							style={{
								background: `linear-gradient(135deg, ${accent} 0%, #33a884 100%)`,
							}}
						>
							{t("salon2.hero.cta1")} <ChevronRight size={18} />
						</a>
					</motion.div>

					{/* Trust line */}
					<motion.p
						className="text-sm"
						style={{ color: "#888" }}
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						transition={{ duration: 0.6, delay: 0.5 }}
					>
						{t("salon2.hero.trust")}
					</motion.p>
				</div>

				{/* Mockup images — 3 phones + notifications */}
				<div className="relative w-full flex justify-center">
					{/* Mobile layout — fade only, no continuous float */}
					<div className="flex md:hidden items-end justify-center relative h-[340px] w-full max-w-sm">
						<motion.img
							src={mockupLeft}
							alt="Reservas online"
							className="absolute left-1/2 bottom-0 w-44 h-auto drop-shadow-2xl -translate-x-[72%] z-10"
							initial={{ opacity: 0, y: 24 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.5, ease: "easeOut" }}
						/>
						<motion.img
							src={mockupCenter}
							alt="App salón de belleza"
							className="w-52 h-auto drop-shadow-2xl relative z-20"
							initial={{ opacity: 0, y: 24 }}
							animate={{ opacity: 1, y: 20 }}
							transition={{ duration: 0.5, ease: "easeOut", delay: 0.1 }}
						/>
						<motion.img
							src={mockupRight}
							alt="Web salón de belleza"
							className="absolute right-1/2 bottom-0 w-44 h-auto drop-shadow-2xl translate-x-[72%] z-10"
							initial={{ opacity: 0, y: 24 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.5, ease: "easeOut", delay: 0.2 }}
						/>

						<NotificationCard
							title={t("salon2.hero.notifications.new.title")}
							line1={t("salon2.hero.notifications.new.line1")}
							line2={t("salon2.hero.notifications.new.line2")}
							className="w-max right-1 left-auto top-1"
							delay={0.7}
							float={false}
							direction="y"
						/>
						<NotificationCard
							title={t("salon2.hero.notifications.reminder.title")}
							line1={t("salon2.hero.notifications.reminder.line1")}
							line2={t("salon2.hero.notifications.reminder.line2")}
							className="w-max left-1 top-[46%]"
							delay={0.95}
							float={false}
							direction="x"
						/>
					</div>

					{/* Desktop layout */}
					<div className="hidden md:flex items-end justify-center relative -space-x-32 lg:-space-x-40">
						<motion.img
							src={mockupLeft}
							alt="Reservas online"
							className="w-44 lg:w-60 h-auto drop-shadow-2xl -rotate-6 z-20"
							initial={{ opacity: 0, x: -80, y: 30 }}
							animate={{
								opacity: 1,
								x: 0,
								y: [0, 6, 0],
								rotate: -6,
							}}
							transition={{
								opacity: { duration: 0.6, delay: 0.2 },
								x: { duration: 0.6, delay: 0.2 },
								y: {
									duration: 6,
									repeat: Infinity,
									ease: "easeInOut",
								},
							}}
						/>
						<motion.img
							src={mockupCenter}
							alt="App salón de belleza"
							className="w-52 lg:w-72 h-auto drop-shadow-2xl z-30"
							initial={{ opacity: 0, y: 80 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.8, ease: "easeOut" }}
						/>
						<motion.img
							src={mockupRight}
							alt="Web salón de belleza"
							className="w-44 lg:w-60 h-auto drop-shadow-2xl rotate-6 z-20"
							initial={{ opacity: 0, x: 80, y: 30 }}
							animate={{
								opacity: 1,
								x: 0,
								y: [0, 6, 0],
								rotate: 6,
							}}
							transition={{
								opacity: { duration: 0.6, delay: 0.4 },
								x: { duration: 0.6, delay: 0.4 },
								y: {
									duration: 6,
									repeat: Infinity,
									ease: "easeInOut",
								},
							}}
						/>

						{/* Notificación nueva reserva — arriba derecha */}
						<NotificationCard
							title={t("salon2.hero.notifications.new.title")}
							line1={t("salon2.hero.notifications.new.line1")}
							line2={t("salon2.hero.notifications.new.line2")}
							className="w-[190px] lg:w-[210px] -right-2 lg:-right-6 top-6 lg:top-10"
							delay={1}
							floatDelay={1.6}
							direction="y"
						/>
						{/* Notificación recordatorio — izquierda media */}
						<NotificationCard
							title={t("salon2.hero.notifications.reminder.title")}
							line1={t("salon2.hero.notifications.reminder.line1")}
							line2={t("salon2.hero.notifications.reminder.line2")}
							className="w-[175px] lg:w-[195px] left-20 lg:left-28 top-[38%] lg:top-[42%]"
							delay={1.45}
							floatDelay={2}
							direction="x"
						/>
					</div>
				</div>
			</div>

			{/* Wave bottom */}
			<div
				className="absolute bottom-0 left-0 w-full overflow-hidden leading-none"
				style={{ marginBottom: -1 }}
			>
				<svg
					className="relative block w-full h-[80px] sm:h-[100px]"
					viewBox="0 0 1440 80"
					xmlns="http://www.w3.org/2000/svg"
					preserveAspectRatio="none"
				>
					<path
						d="M0,80 C480,20 960,140 1440,20 L1440,80 L0,80 Z"
						fill="#ffffff"
					/>
				</svg>
			</div>
		</section>
	);
}
